/** Admin invite dialog. Opened from the users list toolbar. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as groupsApi from '@/api/groups';
import * as usersApi from '@/api/users';
import { useSnackbar } from '@/components/Snackbar';

const Schema = z.object({
  email: z.string().email(),
  first_name: z.string().max(150).optional().default(''),
  last_name: z.string().max(150).optional().default(''),
  group_ids: z.array(z.number()).optional().default([]),
});
type Input = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function UserInviteDialog({ open, onClose }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ user_id: number; invite_code: string } | null>(null);

  const groupsQuery = useQuery({
    queryKey: ['groups', 'all'],
    queryFn: () => groupsApi.list({}),
    enabled: open,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: { email: '', first_name: '', last_name: '', group_ids: [] },
  });

  const inviteMutation = useMutation({
    mutationFn: (input: Input) =>
      usersApi.invite({
        email: input.email,
        first_name: input.first_name,
        last_name: input.last_name,
        group_ids: input.group_ids,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['consoleUsers'] });
      setSuccess({ user_id: data.user_id, invite_code: data.invite_code });
      snackbar.show('Invite sent.', 'success');
    },
    onError: (e: unknown) => {
      setServerError(e instanceof ConsoleApiError ? e.message : 'Could not send invite.');
    },
  });

  const onSubmit = handleSubmit((values) => {
    setServerError(null);
    inviteMutation.mutate(values);
  });

  function handleClose() {
    reset();
    setServerError(null);
    setSuccess(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite system user</DialogTitle>
      <DialogContent dividers>
        {success ? (
          <Stack spacing={2}>
            <Alert severity="success">Invite created for user ID {success.user_id}.</Alert>
            <TextField
              label="Invite code (sent by email)"
              value={success.invite_code}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        ) : (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                autoFocus
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email')}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="First name"
                  fullWidth
                  error={Boolean(errors.first_name)}
                  helperText={errors.first_name?.message}
                  {...register('first_name')}
                />
                <TextField
                  label="Last name"
                  fullWidth
                  error={Boolean(errors.last_name)}
                  helperText={errors.last_name?.message}
                  {...register('last_name')}
                />
              </Stack>
              <Controller
                control={control}
                name="group_ids"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="group-picker-label">Initial groups</InputLabel>
                    <Select
                      labelId="group-picker-label"
                      multiple
                      value={field.value ?? []}
                      onChange={(e) =>
                        field.onChange(
                          typeof e.target.value === 'string'
                            ? e.target.value.split(',').map(Number)
                            : (e.target.value as number[]),
                        )
                      }
                      label="Initial groups"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((id) => {
                            const g = groupsQuery.data?.results.find((x) => x.id === id);
                            return <Chip key={id} label={g?.name ?? `Group #${id}`} />;
                          })}
                        </Box>
                      )}
                    >
                      {groupsQuery.data?.results.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {serverError && <Alert severity="error">{serverError}</Alert>}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send invite'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
